// pages/OurPolicies.jsx
import React, { useState, useEffect } from "react";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes, FaBook } from "react-icons/fa";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = "https://backend.dlapakistan.com/api/ourpolicies";

function OurPolicies() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  
  // New state for policies
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const greenColor = "#70B444";

  // Fetch policies on component mount
  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/`);
      
      // Har policy ke items fetch karen
      const policiesWithItems = await Promise.all(
        response.data.map(async (policy) => {
          try {
            const itemsResponse = await axios.get(`${API_BASE_URL}/${policy.slug}/items/`);
            return {
              ...policy,
              items: itemsResponse.data,
              showItems: false
            };
          } catch (err) {
            console.error(`Error fetching items for ${policy.slug}:`, err);
            return {
              ...policy,
              items: [],
              showItems: false
            };
          }
        })
      );
      
      setPolicies(policiesWithItems);
      setError(null);
    } catch (err) {
      console.error("Error fetching policies:", err);
      setError("Failed to load policies. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Window resize aur header height
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [windowWidth]);

  const isMobileView = windowWidth <= 1029;

  const sidebarLinks = [
    { text: "About Us", border: "#ccc", showIcon: false, path: "/About-Dla" },
    { text: "Meet the Board", border: "#ccc", showIcon: true, path: "/About-Dla/MeettheBoard" },
    { text: "Work We Do", border: null, showIcon: true, path: "/About-Dla/WorkWeDo" },
    { text: "Education", border: null, showIcon: false, path: "/About-Dla/WorkWeDo/Education" },
    { text: "Regulation", border: null, showIcon: false, path: "/About-Dla/WorkWeDo/Regulation" },
    { text: "Membership Support", border: "#ccc", showIcon: false, path: "/About-Dla/WorkWeDo/Support" },
    { text: "Our Policies", border: "#ccc", showIcon: true, path: "/About-Dla/OurPolicies" },
    { text: "Become a Member", border: "#ccc", showIcon: true, path: "/About-Dla/BecomeMember/Reasons" },
    { text: "DLA Fellowship", border: null, showIcon: true, path: "/About-Dla/DLAFellowship" },
  ];

  const toggleItems = (policySlug) => {
    setPolicies(
      policies.map((p) =>
        p.slug === policySlug ? { ...p, showItems: !p.showItems } : p
      )
    );
  };

  const navigateToPolicy = (policySlug) => {
    navigate(`/policies/${policySlug}`);
  };

  const navigateToPolicyItem = (policySlug, itemSlug) => {
    navigate(`/policies/${policySlug}/items/${itemSlug}`);
  };

  const handleSidebarClick = (path) => {
    navigate(path);
    if (windowWidth <= 1029) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  // CSS class mobile overlay ke liye
  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";

  // Loading State
  if (loading) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center mt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444] mb-4"></div>
        <p className="text-gray-600">Loading policies...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center mt-20">
        <div className="text-center text-red-600">
          <p className="text-lg font-medium mb-4">{error}</p>
          <button
            onClick={fetchPolicies}
            style={{ backgroundColor: greenColor }}
            className="px-6 py-3 rounded-xl text-white font-medium hover:opacity-90 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      style={{ backgroundColor: "transparent" }}
    >
      
      {/* Main Container */}
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb section */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/About-Dla")}
          >
            About The DLA
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Our Policies
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          
          {/* DESKTOP SIDEBAR */}
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">About The DLA</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                >
                  {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* MAIN CONTENT AREA */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                Our Policies
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* Empty State */}
            {policies.length === 0 ? (
              <div className="text-center py-24 border-2 border-dashed border-gray-300 rounded-3xl bg-transparent">
                <FaBook className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                <h3 className="text-2xl font-bold text-gray-400 mb-4">No Policies Found</h3>
                <p className="text-gray-500 mb-6">No policies have been created yet.</p>
              </div>
            ) : (
              /* Policies List */
              <div className="space-y-8">
                {policies.map((policy) => (
                  <div
                    key={policy.id}
                    className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        {/* Policy Title - Clickable */}
                        <h2
                          className="text-2xl font-bold cursor-pointer text-[#70B444] hover:text-[#5a9436] transition-colors duration-200"
                          onClick={() => navigateToPolicy(policy.slug)}
                          title="Click to view policy contents"
                        >
                          {policy.title}
                        </h2>
                        
                        {/* Policy Description */}
                        {policy.heading && (
                          <p className="text-gray-600 mt-2 mb-3">{policy.heading}</p>
                        )}
                        
                        {/* Policy Slug */}
                        <div className="flex items-center text-gray-500 text-sm mb-3">
                          <span className="bg-gray-100 px-3 py-1 rounded-lg font-mono">
                            /{policy.slug}
                          </span>
                          <span className="ml-3 text-gray-400">
                            {policy.items.length} section{policy.items.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Toggle Button */}
                      <button
                        onClick={() => toggleItems(policy.slug)}
                        className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-4 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm"
                      >
                        {policy.showItems ? (
                          <>
                            <ChevronUp size={20} />
                            <span>Hide Sections</span>
                          </>
                        ) : (
                          <>
                            <ChevronDown size={20} />
                            <span>Show Sections</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Items List */}
                    {policy.showItems && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        {policy.items && policy.items.length > 0 ? (
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-700 mb-3">
                              Policy Sections:
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {policy.items.map((item) => (
                                <div
                                  key={item.id}
                                  className="group cursor-pointer p-4 bg-gray-50 rounded-2xl hover:bg-[#f0f7e9] border border-gray-200 hover:border-[#70B444] transition-all duration-300"
                                  onClick={() => navigateToPolicyItem(policy.slug, item.slug)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <h4 className="text-lg font-medium text-[#70B444] group-hover:text-[#5a9436]">
                                        {item.title}
                                      </h4>
                                      {item.heading && (
                                        <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                                          {item.heading}
                                        </p>
                                      )}
                                      <div className="mt-2 flex items-center">
                                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                          /{item.slug}
                                        </span>
                                        <span className="ml-2 text-[#70B444] text-sm group-hover:underline">
                                          View Content →
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                              <FaBook className="text-gray-400" />
                            </div>
                            <p className="text-gray-400 mb-2">No sections in this policy</p>
                            <p className="text-gray-500 text-sm">
                              This policy has no sections yet.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MOBILE SIDEBAR */}
          {isMobileView && (
            <div
              className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isSidebarOpen ? "translate-x-0" : "translate-x-full"
              }`}
              style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                  <span className="font-bold text-lg">Navigation</span>
                  <FaTimes 
                    className="cursor-pointer text-2xl" 
                    onClick={toggleSidebar} 
                  />
                </div>
                <div className="overflow-y-auto flex-1 p-2">
                  {sidebarLinks.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSidebarClick(item.path)}
                      className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                        location.pathname === item.path 
                          ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                          : "text-gray-700"
                      }`}
                    >
                      {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Overlay */}
          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        [class*="bg-white"] {
          animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}

export default OurPolicies;